<script lang="ts">
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import Flash from "@shared/services/flashv2.js";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import OrdersView from "@modern/components/distributor/OrdersView.svelte";
  import SalesforceChat from "@shared/services/salesforce_chat";
  import Spinner from "@shared/svelte/Components/SpinnerInternal.svelte";

  import { HttpPromise, HttpPromiseWithEntireResponse } from "@shared/svelte/Modules/ApiService";
  import { onMount } from "svelte";
  import { params } from "@roxi/routify";
  import type { RetailerShowType } from "@shared/types/Retailer";

  let paginationEntries = 0;
  let paginationLimit = 50;
  let retailer: RetailerShowType;
  let retailerId = $params.retailerId;
  let managers;
  let orders = [];
  let accountNumberClass = "";
  let canOpenModal = false;
  let showEditAccountNumberModal = false;
  let editedAccountNumber = "";

  onMount(async () => {
    SalesforceChat.initialize();
  });

  const load = async () => {
    await Promise.all([managerRes(), ordersRes(), retailerRes()]);
    accountNumberEditable();
  };

  const updateAccountNumber = async () => {
    try {
      await HttpPromise({
        method: "PUT",
        url: `/api/distributor/retailer_distributors/${retailer.retailer_distributor_id}`,
        data: { retailer_distributor: { account_number: editedAccountNumber } },
      });
      retailer.account_number = editedAccountNumber;
      showEditAccountNumberModal = false;
      Flash.now({
        type: "success",
        message: "Account Number updated.",
      });
    } catch {
      Flash.now({
        type: "error",
        message:
          "That account number is not associated with an existing account for this distributor.",
      });
    }
  };

  const managerRes = async () => {
    try {
      let res = await HttpPromise({
        method: "GET",
        url: "/api/distributor/sales_reps",
        data: { managed: true },
      });
      managers = res;
    } catch {
      managers = [];
    }
  };

  const ordersRes = async () => {
    let res = await HttpPromiseWithEntireResponse({
      method: "GET",
      url: "/api/distributor/orders",
      data: { includeEvents: true, restrictToSingleDistributor: true, ...$params },
    });

    if (res.ok) {
      paginationEntries = Number(res.headers.get("X-Pagination-Entries"));
      paginationLimit = Number(res.headers.get("X-Pagination-Limit"));
      orders = await res.json();
    }
  };

  const retailerRes = async () => {
    try {
      let res = await HttpPromise({
        method: "GET",
        url: `/api/distributor/retailers/${retailerId}`,
      });
      retailer = res;
    } catch {
      Flash.now({
        type: "error",
        message: "That retailer is not active, please choose another.",
      });
    }
  };

  const cityStateZip = () => {
    return [retailer.city, retailer.state, retailer.zip].filter(n => n).join(", ");
  };

  const accountNumberEditable = () => {
    if (retailer.modifiable_account_numbers) {
      accountNumberClass = "fizz-highlight-on-hover cursor-pointer fizz-link";
      canOpenModal = true;
    }
  };

  const close = () => {
    showEditAccountNumberModal = false;
  };

  const open = () => {
    if (canOpenModal) {
      showEditAccountNumberModal = true;
    }
  };
</script>

<style>
  .retailer-margin {
    margin-inline: 15rem;
  }

  .account-number-margin {
    margin-inline: 0.5rem;
  }
</style>

{#await load()}
  <Spinner />
{:then}
  <div class="distributor-page">
    <FizzStyles>
      <div
        class="fizz-stack-32 fizz-surface fizz-flex-row fizz-inset-square-16 fizz-space-between account-number-widget"
      >
        <h4 class="fizz-heading-4">{retailer.name}</h4>
        {#if retailer.account_number}
          <h5 class="fizz-heading-5"
            >Account Number: <span
              class={accountNumberClass}
              on:click={open}
              id="click-edit-account-number">{retailer.account_number}</span
            >
          </h5>
        {:else if retailer.modifiable_account_numbers}
          <h5
            class={`fizz-heading-5 ${accountNumberClass}`}
            on:click={open}
            id="click-add-account-number">Add Account Number</h5
          >
        {/if}
        <Modal bind:show={showEditAccountNumberModal} fizzModal={true} size="medium">
          <div class="layout-header-footer" data-testid="add-inventory-modal">
            <header class="fizz-modal-header fizz-inset-horizontal-8">
              <div class="fizz-heading-5">Edit Account Number</div>
              <div class="fizz-modal-close" on:click={close}>
                <Icon
                  type="X"
                  extraClassNames="fizz-text-brand cursor-pointer"
                  height="15"
                  width="15"
                />
              </div>
            </header>
            <input
              class={"fizz-flex-item fizz-input-text account-number-margin fizz-stack-16"}
              type="text"
              id="edit-account-number"
              bind:value={editedAccountNumber}
              placeholder={retailer.account_number || "Account Number"}
            />
          </div>
          <div class="fizz-flex-row">
            <div class="fizz-text-right fizz-full-width">
              <button
                on:click={updateAccountNumber}
                disabled={editedAccountNumber == "" ? true : false}
                class="fizz-button-primary fizz-button-medium fizz-inline-block quarter-width"
                type="submit"
              >
                Save
              </button>
              <button
                class="fizz-button-secondary fizz-button-medium fizz-inline-block quarter-width"
                on:click={close}
              >
                <span>Cancel</span>
              </button>
            </div>
          </div>
        </Modal>
      </div>

      <div class="fizz-inset-square-16">
        <div
          class="fizz-stack-8 fizz-inset-square-16 fizz-flex-row fizz-space-between fizz-surface fizz-container-small"
        >
          <div>
            <p class="fizz-text-strong">Address</p>
            <p>{retailer.address1}</p>
            {#if retailer.address2}
              <p>{retailer.address2}</p>
            {/if}
            <p>{cityStateZip()}</p>
          </div>
          <div>
            <p class="fizz-text-strong">Regular Order Day</p>
            <p>{retailer.order_day || "N/A"}</p>
          </div>
        </div>
        {#if retailer.users}
          {#each retailer.users as user}
            <div
              class="fizz-stack-32 fizz-inset-square-16 fizz-flex-row-responsive fizz-space-between fizz-surface fizz-container-small"
            >
              <div>
                {`${user.first_name} ${user.last_name}`}
              </div>
              <p>
                {user.email}
              </p>
            </div>
          {/each}
        {/if}
      </div>
      <OrdersView
        bind:ordersRes={orders}
        {paginationLimit}
        {paginationEntries}
        managed={managers}
        {load}
      />
    </FizzStyles>
  </div>
{/await}
